import { inject, Injectable } from '@angular/core';
import { createClient } from '@workos-inc/authkit-js';
import { environment } from '../../environments/environment';
import { AuthService, ExchangeCodeResponse } from './auth.service';
import { PAGE_ROUTES, SESSION_KEY_NAME } from '../constants';
import { showSnackBar } from '../utils/snackBar.ulti';
import {
    MatSnackBar,
} from "@angular/material/snack-bar";
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';

export type AuthKitClient = Awaited<ReturnType<typeof createClient>>;

export type SignInParams = {
    loginHint?: string
}

export type IsSamePreLoginHintParams = {
    sessionId: string,
    preLoginHint: string,
    loginHint: string
}

@Injectable({
    providedIn: 'root',
})
export class AuthkitService {
    private _authKit!: AuthKitClient;
    private _isInitialized = false;
    private _snackBar = inject(MatSnackBar);
    private _invalidOrExpiredCodeNotiMessage = "The code has expired or is invalid."

    constructor(
        private _authService: AuthService,
        private _translate: TranslateService,
        private _router: Router
    ) { }

    async init({ redirectUri }: { redirectUri: string }): Promise<void> {
        if (!this._isInitialized) {
            this._authKit = await createClient(environment.workosClientId, {
                devMode: !environment.production,
                redirectUri
            });

            this._isInitialized = true;
        }
    };

    signUp() {
        const sessionId = localStorage.getItem(SESSION_KEY_NAME.WORKOS.session_id) ?? ''

        if (sessionId) {
            this._authService.signOutBySessionId({ sessionId: sessionId as string }).subscribe(
                async () => {
                    await this._authKit.signUp();
                })
        } else {
            this._authKit.signUp()
        }
    }

    async signIn({ loginHint }: SignInParams) {
        const sessionId = localStorage.getItem(SESSION_KEY_NAME.WORKOS.session_id) ?? ''

        if (!sessionId) {
          await this._authKit.signIn({ loginHint });
        } else {
          this._authService.signOutBySessionId({ sessionId: sessionId as string }).subscribe(async () => {
            await this._authKit.signIn({ loginHint });
          });
        }
    }

    async getAccessToken(): Promise<string | null> {
        if (this._isInitialized) {
            return await this._authKit.getAccessToken()
        }

        return null
    }

    signOut({ redirectUri, navigate = false }: { redirectUri?: string, navigate: boolean }) {
        if (navigate) { this._authKit.signOut({ returnTo: redirectUri, navigate: true }) }
        else { this._authKit.signOut({ returnTo: redirectUri, navigate: false }) }
    }

    authenticateByCode(code: string): void {
        this._authService
            .exchangeCode(code)
            .subscribe({
                next: (result: ExchangeCodeResponse) => {
                    const { access_token, user, session_id } = result

                    // Store the session email and session_id for authenticate success with user and password
                    localStorage.setItem(SESSION_KEY_NAME.WORKOS.previous_session_login_hint, user.email)
                    localStorage.setItem(SESSION_KEY_NAME.WORKOS.session_id, session_id)

                    // Check if user has multiple accounts (admin + employee)
                    if (result.accounts && result.accounts.length > 1) {
                        const queryParams = {
                            accounts: JSON.stringify(result.accounts),
                            accessToken: access_token,
                            sessionId: session_id,
                            loginHint: user.email
                        };
                        this._router.navigate([PAGE_ROUTES.ACCOUNT_SELECTION], { queryParams });

                        if (this._authKit)
                            this._authKit.dispose();
                        return;
                    }

                    const redirectUri = result.app_type_id === 1
                        ? `${environment.adminRedirectUri}/#/login`
                        : `${environment.defaultRedirectUri}/login`;

                    // Redirect to success page
                    const queryParams = { accessToken: access_token, redirectUri, sessionId: session_id }
                    this._router.navigate([PAGE_ROUTES.SUCCESS], { queryParams })

                    // Dispose authkit if has
                    if (this._authKit)
                        this._authKit.dispose()
                },
                error: () => {
                    showSnackBar({ type: 'error', message: this._invalidOrExpiredCodeNotiMessage, params: { code }, snackBar: this._snackBar, translate: this._translate })
                    this._router.navigate([PAGE_ROUTES.HOME])
                }
            })
    }
}
